'use client';

import * as React from 'react';
import type { CultivAvmScene } from '../types';
import { MARKETING_CDN } from '../site';
import './valuation-scene.css';

/* Scene-3 valuation loop — act 1 is a real screen recording of the AVM step-2
   comparables screen on this scene's own lead (Avenue de Mai 118): real map,
   real comparables, the estimate updating as they are selected. The stage then
   shrink-morphs into the Methode A card and the scene-2 circuit takes over:
   Analytisch + Rendement activate, the AI merge assembles the REAL report
   cover (production agency_demo-cultiv page-01, fixtures baked in).
   The clip is NL-only for now; fr/en recordings drop into VIDEO_BY_LOCALE. */

/* act-2 circuit, in the 1220×640 canvas: each method card's right edge (x 360)
   runs to the AI chip's left edge (x 500, centre y 320), then on to the cover. */
const WIRES = [
  { d: 'M360 130 H460 Q480 130 480 150 V300 Q480 320 500 320', kf: 's3d-w1' },
  { d: 'M360 320 H500', kf: 's3d-w2' },
  { d: 'M360 510 H460 Q480 510 480 490 V340 Q480 320 500 320', kf: 's3d-w3' },
  { d: 'M640 320 H756', kf: 's3d-w4' },
];
const PORTS: Array<[number, number]> = [
  [360, 130], [360, 320], [360, 510], [500, 320], [640, 320], [756, 320],
];

/* the recorded AVM clip, one take per locale, on the marketing CDN. Version the
   filename on every re-shoot rather than overwriting — CloudFront caches these
   for a year, and footage in the repo would put a few MB in git history per cut. */
const VIDEO_BY_LOCALE: Record<string, string> = {
  nl: `${MARKETING_CDN}/s3-avm-nl-v1`,
  fr: `${MARKETING_CDN}/s3-avm-fr-v1`,
  en: `${MARKETING_CDN}/s3-avm-en-v1`,
};
const videoFor = (locale: string) => VIDEO_BY_LOCALE[locale] ?? VIDEO_BY_LOCALE.nl;

/* Every take is cut to the same 13.27s (each at whatever speed its raw length
   needed), so all three locales share one loop. Act 1 is 0-40% of the loop in
   the stylesheet, hence clip / 0.4. */
const CLIP_SECONDS = 13.27;
export const VALUATION_LOOP_MS = Math.round((CLIP_SECONDS / 0.4) * 1000);

export function ValuationSceneMock({ avm, locale }: { avm: CultivAvmScene; locale: string }) {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const canvasRef = React.useRef<HTMLDivElement>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [scale, setScale] = React.useState(0);

  // Keep the clip locked to the CSS loop: rewind and replay it once per scene
  // iteration, otherwise the clip and the loop drift apart within a few cycles.
  // The name/target guard is load-bearing: animationiteration BUBBLES, so every
  // descendant animation reaches this listener — including the AI chip's 1.6s
  // shimmer, which restarted the video twice a second.
  React.useEffect(() => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;
    const restart = () => {
      video.currentTime = 0;
      void video.play().catch(() => {});
    };
    const onIteration = (e: AnimationEvent) => {
      if (e.target === canvas && e.animationName === 's3d-scene') restart();
    };
    restart();
    canvas.addEventListener('animationiteration', onIteration);
    return () => canvas.removeEventListener('animationiteration', onIteration);
  }, []);

  React.useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    // offsetWidth = layout width, immune to ancestor reveal-transforms (see DashboardMock)
    const fit = () => setScale(el.offsetWidth / 1220);
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const video = videoFor(locale);

  return (
    <div ref={rootRef} className="scene-canvas s3d" aria-hidden="true">
      <div ref={canvasRef} className="s3d-canvas" style={{ transform: `scale(${scale})`, visibility: scale ? 'visible' : 'hidden' }}>

        {/* the dotted plate: act 2's ground, faded in as the recording leaves */}
        <div className="s3d-plate" />

        {/* circuit wires + ports */}
        <svg className="s3d-wires" width="1220" height="640" viewBox="0 0 1220 640" fill="none">
          {WIRES.map((w) => (
            <path key={w.d} d={w.d} stroke="rgba(40,40,40,.18)" strokeWidth="1.5" fill="none" />
          ))}
          {WIRES.map((w) => (
            <path key={w.kf} className="s3d-wire" d={w.d} stroke="#E9551B" strokeWidth="2" strokeLinecap="round" fill="none" pathLength={100} style={{ animation: `${w.kf} 33s linear infinite both` }} />
          ))}
        </svg>
        <svg className="s3d-ports" width="1220" height="640" viewBox="0 0 1220 640" fill="none">
          {PORTS.map(([cx, cy]) => (
            <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="3" fill="#fff" stroke="rgba(40,40,40,.3)" strokeWidth="1.5" />
          ))}
        </svg>

        {/* ── ACT 1: the recorded AVM step-2 screen ── */}
        <div className="s3d-stage">
          <video ref={videoRef} muted playsInline preload="auto" poster={`${video}.jpg`} key={video}>
            <source src={`${video}.webm`} type="video/webm" />
            <source src={`${video}.mp4`} type="video/mp4" />
          </video>
        </div>

        {/* ── ACT 2: Methode A (compact, appears as the morph lands) ── */}
        <div className="s3d-card s3d-card--a">
          <div className="s3d-mh">
            <span className="badge"><span className="kq-cap">{avm.methodA}</span></span>
            <b className="kq-cap">{avm.methodATab}</b>
          </div>
          <div className="s3d-minimap">
            {/* still crop of the recording's Explorer map — same pins the clip shows */}
            {/* eslint-disable-next-line @next/next/no-img-element -- map still in a scaled canvas */}
            <img src="/cultiv/s3-avm-map.jpg" alt="" />
          </div>
          <div className="s3d-res"><em>{avm.estLabel}</em><b>{avm.aResultV}</b></div>
        </div>

        {/* Methode B — analytical */}
        <div className="s3d-card s3d-card--b">
          <div className="s3d-mh">
            <span className="badge"><span className="kq-cap">{avm.methodB}</span></span>
            <b className="kq-cap">{avm.methodBTab}</b>
          </div>
          <div className="s3d-row s3d-row--1"><span>{avm.bLand}</span><span className="v">{avm.bLandV}</span></div>
          <div className="s3d-row s3d-row--2"><span>{avm.bBuild}</span><span className="v">{avm.bBuildV}</span></div>
          <div className="s3d-row s3d-row--3"><span>{avm.bVet}</span><span className="v">{avm.bVetV}</span></div>
          <div className="s3d-res s3d-res--b"><em>{avm.bResult}</em><b>{avm.bResultV}</b></div>
        </div>

        {/* Methode C — yield */}
        <div className="s3d-card s3d-card--c">
          <div className="s3d-mh">
            <span className="badge"><span className="kq-cap">{avm.methodC}</span></span>
            <b className="kq-cap">{avm.methodCTab}</b>
          </div>
          <div className="s3d-row s3d-row--c1"><span>{avm.cRent}</span><span className="v">{avm.cRentV}</span></div>
          <div className="s3d-row s3d-row--c2"><span>{avm.cYield}</span><span className="v">{avm.cYieldV}</span></div>
          <div className="s3d-res s3d-res--c"><em>{avm.cResult}</em><b>{avm.cResultV}</b></div>
        </div>

        {/* AI merge */}
        <div className="s3d-ai">
          {/* eslint-disable-next-line @next/next/no-img-element -- decorative mark in a scaled canvas */}
          <img src={`${MARKETING_CDN}/cultiv-symbol-orange.svg`} alt="" />
          <b className="kq-cap">{avm.aiTag}</b>
          <span className="sheen" />
          <span className="s3d-ai-lbl">{avm.ai}</span>
        </div>

        {/* the real report cover — production page-01 markup, fixtures baked in */}
        <div className="s3d-cover">
          <div className="s3d-cover-in">
            <div className="cover-page">
              <div className="cover-photo">
                <span className="cover-badge"><span className="cover-badge-dot" />{avm.coverBadge}</span>
                {/* the agency_demo test-fixture's façade photo (street side) — the shot
                    the onboarding's generated report leads with */}
                {/* eslint-disable-next-line @next/next/no-img-element -- cover photo in a scaled canvas */}
                <img src="https://d1oytcss5issfa.cloudfront.net/valuation_report/report-pdf/user-17-b22cdd9f-383e-4c52-a1a2-2b7b8602d2bd/lead-073a719b-63b0-4782-814e-e41c620f5f02/step1-photos/1782147149154_tempimageeocl3p.jpg" alt="" />
              </div>
              <div className="cover-info">
                <span className="cover-eyebrow">{avm.coverEyebrow}</span>
                <h1 className="cover-title">Avenue de Mai 118</h1>
                <p className="cover-subtitle">{avm.coverSubtitle}</p>
                <div className="cover-divider" />
                <div className="cover-meta">
                  <div className="cover-meta-col">
                    <span className="cover-meta-label">{avm.coverDateLabel}</span>
                    <span className="cover-meta-value">{avm.coverDateV}</span>
                  </div>
                </div>
                <div className="cover-bottom">
                  <span className="cover-contact">{avm.coverContact}</span>
                  <div className="cover-logo">
                    {/* eslint-disable-next-line @next/next/no-img-element -- agency logo in a scaled canvas */}
                    <img src={`${MARKETING_CDN}/cultiv-lockup-orange-symbol.svg`} alt="Cultiv" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* estimated-range annotation pill */}
        <div className="s3d-range">
          <em className="kq-cap">{avm.rangeLabel}</em>
          <b className="kq-cap">{avm.rangeV}</b>
        </div>

      </div>
    </div>
  );
}
